import PropTypes from 'prop-types';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import I18n from 'i18n-js';
import parseFare from 'common/utils/parse-fare';
import { cumulateFare, displayCumulatedFare } from 'common/utils/cumulate-fare';
import {
  deliveriesFromPropsSelector,
  recommendedVehicleTypeSelector,
} from 'selectors/new-booking-selectors';
import _get from 'lodash/get';
import _cloneDeep from 'lodash/cloneDeep';
import { mergeDisplayError } from 'helper/anytime-booking';
import _every from 'lodash/every';
import NewBookingDuck from 'ducks/new-booking-duck';
import NewBulkUploadDuck from 'ducks/new-bulk-upload-duck';
import OverlayBody from '../overlay-body';
import CommonSelector from '../common-section-ui/common-selector';
import styles from '../common-section-ui/styles.less';

const BulkVehicleTypePanel = ({
  dispatch,
  onClose,
  deliveryIDs,
  deliveries,
}) => {
  const onConfirm = useCallback(
    () => {
      onClose();
    },
    [onClose],
  );

  const onBack = useCallback(
    () => {
      onClose();
    },
    [onClose],
  );

  const vehicleTypesToRender = useMemo(
    () => {
      const allVehicleTypesMap = {};
      const cumulatedFares = {};
      const vehicleTypesAvailableCount = {};

      deliveries.forEach(delivery => {
        _get(delivery, 'config.vehicleTypes', []).forEach(vehicleType => {
          const errorMessage = _get(vehicleType, 'errorMessage');
          if (!allVehicleTypesMap[vehicleType.id]) {
            allVehicleTypesMap[vehicleType.id] = _cloneDeep(vehicleType);
          }
          const parsedFare = parseFare(vehicleType.fare);
          cumulateFare(cumulatedFares, vehicleType.id, parsedFare);
          if (!errorMessage) {
            vehicleTypesAvailableCount[vehicleType.id] =
              _get(vehicleTypesAvailableCount, vehicleType.id, 0) + 1;
          } else {
            allVehicleTypesMap[vehicleType.id].errorMessage = errorMessage;
          }
        });
      });

      const allVehicleTypes = Object.values(allVehicleTypesMap);
      allVehicleTypes.forEach(vehicleType => {
        vehicleType.key = vehicleType.id;
        vehicleType.fare = displayCumulatedFare(cumulatedFares[vehicleType.id]);
        vehicleType.disabled =
          !!vehicleType.errorMessage ||
          vehicleTypesAvailableCount[vehicleType.id] < deliveries.length;
        if (vehicleType.errorMessage) {
          const { reason, hint } = mergeDisplayError(vehicleType.errorMessage);
          vehicleType.disabledReason = reason;
          vehicleType.disabledHint = hint;
        }
      });
      return allVehicleTypes.sort((a, b) => a.id.localeCompare(b.id));
    },
    [deliveries],
  );

  const selectedVehicleType = useMemo(
    () => {
      const vehicleTypes = deliveries.map(
        delivery =>
          _get(delivery, 'detail.configuration.vehicleType') ||
          recommendedVehicleTypeSelector(delivery),
      );
      if (
        _every(vehicleTypes, vehicleType => vehicleType === vehicleTypes[0])
      ) {
        return vehicleTypes[0];
      }
      return undefined;
    },
    [deliveries],
  );

  const onSelectVehicleType = useCallback(
    vehicleType => {
      dispatch(
        NewBookingDuck.creators.bulkUpdateServiceConfig(deliveryIDs, {
          vehicleType: _get(vehicleType, 'id'),
        }),
      );
      dispatch(NewBulkUploadDuck.creators.trySetOfferConfig(null));
    },
    [dispatch, deliveryIDs],
  );

  return (
    <OverlayBody
      showConfirm
      disabledConfirm={selectedVehicleType === undefined}
      onConfirmClick={onConfirm}
      showHeader
      showTitle
      title={I18n.t('bulkUploadRevamp.overlay.vehicleType.title')}
      showClose
      onClose={onBack}
    >
      <div className={styles.section}>
        <CommonSelector
          options={vehicleTypesToRender}
          selectedKey={selectedVehicleType}
          onSelect={onSelectVehicleType}
        />
      </div>
    </OverlayBody>
  );
};

BulkVehicleTypePanel.propTypes = {
  dispatch: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  deliveryIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  deliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
};

export default connect((state, props) => ({
  deliveries: deliveriesFromPropsSelector(state, props),
}))(BulkVehicleTypePanel);
